import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { FC, useMemo, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { generateTabsItems } from '@utils/generateTabsItems';
import { readingsSubRoutes } from 'src/router/Readings.routes';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { useAuthSlice } from '@restore/auth/auth.slice';
import {
  useClinicalTrialTabBreadcrumbs,
  TabBreadcrumbMap,
} from '@hooks/useClinicalTrialTabBreadcrumbs';
import { UserRoles } from '@dynamika-shared/types/auth';

type HeaderAction = {
  buttonText: string;
  onClick: () => void;
};

type ReadingsOutletContext = {
  setTaskDefinitionHeaderAction: (action?: HeaderAction) => void;
};

// NOTE: Single source of truth for the breadcrumb shown on each Reading Config
// tab. Keyed by the URL segment so the breadcrumb always matches the active tab.
const readingConfigTabBreadcrumbs: TabBreadcrumbMap = {
  'task-definition': ['Task Definition', 'taskDefinition'],
  'reading-inventory': ['Inventory', 'readingInventory'],
};

export const ReadingsClinicalTrial: FC = () => {
  const isPM = useAuthSlice(
    s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER
  );

  const { pathname } = useLocation();
  const { id } = useParams();
  const [taskDefinitionHeaderAction, setTaskDefinitionHeaderAction] =
    useState<HeaderAction>();

  const items = useMemo(
    () =>
      generateTabsItems({
        routes: readingsSubRoutes,
        currentPathname: '',
        id,
        disableStyles: true,
      }),
    [id]
  );

  const currentPathname = useMemo(() => {
    const chunks = pathname.split('/');
    return chunks?.[4];
  }, [pathname]);

  const isReadingInventory = currentPathname?.includes('reading-inventory');
  const isTaskDefinition = currentPathname?.includes('task-definition');

  useClinicalTrialTabBreadcrumbs(
    readingConfigTabBreadcrumbs,
    currentPathname,
    'task-definition'
  );

  const outletContext = useMemo<ReadingsOutletContext>(
    () => ({
      setTaskDefinitionHeaderAction,
    }),
    [setTaskDefinitionHeaderAction]
  );

  return (
    <div className={'flex flex-col'}>
      <div>
        <header className={'flex justify-between items-center'}>
          <Tabs activeKey={currentPathname} items={items} mode={'button'} />
          <div className='flex items-center gap-2'>
            {isTaskDefinition && !isPM && taskDefinitionHeaderAction && (
              <Button
                iconType={'plus'}
                iconClassName={'w-2 h-2'}
                className='w-[14.4rem] !border-blackOlive !bg-blackOlive text-white hover:!border-dodgerBlue hover:!bg-dodgerBlue'
                onClick={taskDefinitionHeaderAction.onClick}
              >
                {taskDefinitionHeaderAction.buttonText}
              </Button>
            )}
            {isReadingInventory && !isPM && (
              <Button
                className='!bg-blackOlive !text-1420 hover:!bg-dodgerBlue text-white !px-4'
                htmlType={'submit'}
                iconType={'plus'}
                iconClassName={'w-2 h-2'}
                form={'clinical-trial-inventory-table'}
              >
                Add
              </Button>
            )}
          </div>
        </header>
      </div>
      <div className={'grow'}>
        <Outlet context={outletContext} />
      </div>
    </div>
  );
};
